import { useContext, useEffect, useState } from 'react'
import { Form, message } from 'antd'
import { useParams } from 'react-router-dom'

import AuthContext from 'contexts/AuthContext'
import { commonHelper } from 'helpers'

import { useGetCompanies } from 'services/company/company-service'
import { useCustomMutation } from 'services/shared/mutation'
import { EditUserPayload } from 'services/shared/mutation.payload'
import { editUserMutation } from 'gql/user'
import { useGetUserById } from 'services/users/users.service'

const useProfileTabContent = () => {
  const { user, refetch } = useContext(AuthContext)
  const { userId } = useParams()
  const { user: userToEdit, loading: userToEditLoading } = useGetUserById(userId as string)
  const editUser = useCustomMutation<EditUserPayload>({ mutation: editUserMutation })
  const { companies } = useGetCompanies()

  const [companyId, setCompanyId] = useState(userToEdit.companyId || '')
  const [loading, setLoading] = useState(false)
  const [options, setOptions] = useState([])
  const [form] = Form.useForm()
  const [companyUser, setCompanyUser] = useState(false)

  useEffect(() => {
    if (!userToEditLoading && userToEdit) {
      setCompanyId(userToEdit.companyId || '')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userToEditLoading])

  useEffect(() => {
    if (user.role === 'superAdmin') {
      setOptions(
        companies.map((company: any) => ({
          value: company._id,
          label: company.name,
        })),
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companies])

  const handleSubmit = async () => {
    setLoading(true)

    // Get all form field values including the new ones
    const formData = form.getFieldsValue([
      'firstName',
      'lastName',
      'email',
      'phoneNumber',
      'mobileNumber',
      'accountType',
      'isDirectlyAuthorised',
      'networkDetails',
      'fcaNumber',
    ])

    // Check required fields
    const requiredFields = ['firstName', 'lastName', 'email', 'phoneNumber', 'accountType']
    const isEveryRequiredFieldFilled = requiredFields.every((fieldKey: string) => !!formData[fieldKey])

    if (!isEveryRequiredFieldFilled) {
      message.info('Please fill out all the required fields')
      setLoading(false)
      return
    }

    // Validate FCA number if provided
    const fcaNumber = String(formData.fcaNumber || '')
    if (fcaNumber && !(fcaNumber.length === 6 || fcaNumber.length === 7)) {
      message.info('Please check FCA number, it should be 6 or 7 digits')
      setLoading(false)
      return
    }

    const { errors } = await editUser({ ...formData, companyId, fcaNumber: Number(fcaNumber), _id: userId })

    if (errors?.length) {
      setLoading(false)
      const errMessage = commonHelper.parseGraphqlErrorMessage(errors)
      message.error(errMessage)
      return
    }

    message.success('User profile updated successfully')
    setLoading(false)
    refetch()
  }

  const companyUserChange = (e: any) => {
    setCompanyUser(e.target.checked)
  }

  const handleCompanyChange = (value: string) => {
    setCompanyId(value)
  }

  return {
    loading,
    form,
    companyUser,
    companyUserChange,
    handleCompanyChange,
    options,
    handleSubmit,
  }
}

export default useProfileTabContent
